
import React, { useState, useEffect } from "react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface ProfileLoadingProps {
  reason?: string;
  onReload?: () => void;
  onReturnToSignIn?: () => void;
}

const ProfileLoading = ({ 
  reason = "loading your profile information",
  onReload = () => window.location.reload(),
  onReturnToSignIn = () => window.location.href = '/sign-in'
}: ProfileLoadingProps) => {
  const [loadingTime, setLoadingTime] = useState(0);
  
  // Add a timer to track how long we've been loading
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Different messages based on loading time
  const getMessage = () => {
    if (loadingTime > 5) {
      return "Loading is taking longer than expected. You might need to sign in again or check your network connection.";
    } 
    return `${reason.charAt(0).toUpperCase() + reason.slice(1)}...`;
  };

  // Show skeleton loaders for very short loading times
  if (loadingTime < 1) {
    return (
      <div className="flex flex-col justify-center items-center h-full space-y-6 bg-background p-4">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="space-y-2 w-full max-w-md">
          <Skeleton className="h-4 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col justify-center items-center h-full space-y-4 bg-background p-4">
      <Loader className="h-8 w-8 animate-spin text-primary" />
      <h3 className="text-xl font-medium text-center">
        {reason.charAt(0).toUpperCase() + reason.slice(1)}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md text-center">
        {getMessage()}
      </p>
      
      {loadingTime > 2 && (
        <div className="flex flex-col gap-2 w-full max-w-[250px] mt-2">
          <Button 
            onClick={onReturnToSignIn}
            className="w-full"
          >
            Return to Sign In
          </Button>
          
          <Button
            variant="outline"
            onClick={onReload}
            className="w-full"
          >
            Refresh Page
          </Button>

          <p className="text-xs text-muted-foreground text-center mt-2">
            Debug Info: Loading for {loadingTime}s
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfileLoading;
